import React, { useEffect, useState, useCallback } from 'react';
import Navigation from '../components/Navigation';
import { NavLink } from 'react-router-dom';
import base_url from '../config/Config';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Spinner from 'react-bootstrap/Spinner';
import { FaEye, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Cookies from 'js-cookie';
import PageLoader from '../components/PageLoader';

const MultipleLoans = () => {
    const [multipleLoans, setMultipleLoans] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [expandedRows, setExpandedRows] = useState({});
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDownloadingLmaster, setIsDownloadingLmaster] = useState(false);
    const [searchOption, setSearchOption] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [permissions, setPermissions] = useState([]);
    const token = Cookies.get('authToken');

    useEffect(() => {
        const adminId = localStorage.getItem('adminId') || '';
        axios
            .get(`${base_url}/api/auth/tabs/${adminId}`)
            .then((res) => setPermissions(res.data || []))
            .catch(() => setPermissions([]))
            .finally(() => setPageLoading(false));
    }, []);

    const hasPermission = useCallback(
        (tabName, action) => {
            const tab = permissions.find((t) => t.tabName === tabName);
            if (!tab) return false;
            switch (action) {
                case 'view':
                    return !!tab.canView;
                case 'edit':
                    return !!tab.canEdit;
                case 'download':
                    return !!tab.canDelete;
                default:
                    return false;
            }
        },
        [permissions]
    );

   const getAllMultipleLoans = useCallback(() => {
    if (!hasPermission('Multiple Loans', 'view')) return;
    setPageLoading(true);
    axios
        .get(`${base_url}/api/loans/multipleLoans`, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        })
        .then((response) => {
            console.log('Fetched multipleLoans:', response.data);
            const data = response.data;
            if (Array.isArray(data)) {
                setMultipleLoans(data);
            } else if (Array.isArray(data.multipleLoans)) {
                setMultipleLoans(data.multipleLoans);
            } else {
                console.error('Unexpected response format:', data);
                setMultipleLoans([]);
            }
        })
        .catch((error) => {
            console.error('Error fetching multiple loans:', error);
            setMultipleLoans([]);
        })
        .finally(() => setPageLoading(false));
}, [token, hasPermission]);


    useEffect(() => {
        if (token) getAllMultipleLoans();
    }, [token, getAllMultipleLoans]);

    const toggleExpand = (customerId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [customerId]: !prev[customerId],
        }));
    };

    const handleExportLoan = async () => {
        if (!hasPermission('Multiple Loans', 'download')) {
            alert('You do not have permission to download loans.');
            return;
        }
        setIsDownloading(true);
        try {
            const response = await axios.get(`${base_url}/api/export/pending`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` },
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Loan_data.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            alert('Export Loan successfully');
        } catch (error) {
            console.error('Error exporting loan:', error);
            alert('Failed to export loan');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleExportLoanMaster = async () => {
        if (!hasPermission('Multiple Loans', 'download')) {
            alert('You do not have permission to download loan master.');
            return;
        }
        setIsDownloadingLmaster(true);
        try {
            const response = await axios.get(`${base_url}/api/export/loanMaster`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` },
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Loan_Master_data.xlsx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            alert('Export Loan Master successfully');
        } catch (error) {
            console.error('Error exporting loan master:', error);
            alert('Failed to export loan master');
        } finally {
            setIsDownloadingLmaster(false);
        }
    };

    const handleSearch = (event) => {
        event.preventDefault();
        if (searchOption && searchInput) {
            let url = '';
            switch (searchOption) {
                case 'borrower_name':
                    url = `${base_url}/api/loanFilter/search?borrowerName=${searchInput}`;
                    break;
                case 'borrower_id':
                    url = `${base_url}/api/loanFilter/search?loanId=${searchInput}`;
                    break;
                case 'phone':
                    url = `${base_url}/api/loanFilter/search?mobileNumber=${searchInput}`;
                    break;
                default:
                    break;
            }
            axios.get(url).then(
                (response) => setPermissions(response.data),
                (error) => console.log(error)
            );
        }
    };

    // ✅ Pagination setup
    const [currentPage, setCurrentPage] = useState(1);
    const multipleLoansPerPage = 10; // show 10 per page

    const indexOfLastLoan = currentPage * multipleLoansPerPage;
    const indexOfFirstLoan = indexOfLastLoan - multipleLoansPerPage;
    const currentLoans = multipleLoans.slice(indexOfFirstLoan, indexOfLastLoan);
    const totalPages = Math.ceil(multipleLoans.length / multipleLoansPerPage);
    const maxPageButtons = 10;

    const getPaginationRange = () => {
        const mid = Math.floor(maxPageButtons / 2);
        let start = Math.max(1, currentPage - mid);
        let end = Math.min(totalPages, start + maxPageButtons - 1);
        if (end - start < maxPageButtons - 1) start = Math.max(1, end - maxPageButtons + 1);
        return { start, end };
    };

    const { start: startPage, end: endPage } = getPaginationRange();

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) setCurrentPage(pageNumber);
    };

    if (pageLoading) return <PageLoader text="Loading..." />;

    if (!hasPermission('Loan', 'view')) {
        return (
            <div className="text-center mt-5">
                <h3>Access Denied</h3>
                <p>You do not have permission to view this page.</p>
            </div>
        );
    }

    return (
        <>
            <div className='overviewsaa'>
                <h4>Multiple Loans Details</h4>
            </div>
            <div className='navigationss mb-5'>
                <Navigation />
            </div>
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-lg-2 col-md-2 sidebarnavigartion'>
                        <div className='lmssidebar'>
                            <Sidebar />
                        </div>
                    </div>

                    <div className='col-lg-10 col-md-10'>
                        <div className='dashboardlms  text-center'>
                            <div className='row'>

                                {/* 🔍 Search Section */}
                                <div className='col-md-12'>
                                    <div className='searchtoll'>
                                        <form onSubmit={handleSearch}>
                                            <div className='row'>
                                                <div className="col-md-2 col-3">
                                                    <div className=" required">
                                                        <select name="option" id="option" className="form-control"
                                                            value={searchOption} onChange={(e) => setSearchOption(e.target.value)} required>
                                                            <option value="">Search By</option>
                                                            <option value="borrower_name">Borrower Name</option>
                                                            <option value="borrower_id">Loan Id</option>
                                                            <option value="phone">Phone</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="col-md-3 col-3">
                                                    <div className=" required">
                                                        <input type="text" className="form-control" placeholder="Search..."
                                                            value={searchInput} onChange={(e) => setSearchInput(e.target.value)} required />
                                                    </div>
                                                </div>
                                                <div className="col-md-1 col-3">
                                                    <div className=" required iconsss">
                                                        <button type="submit" className="btn btn-sm btn-primary">Search</button>
                                                    </div>
                                                </div>

                                                {/* 🟢 Download Buttons */}
                                                <div className="col-md-3 mt-2">
                                                    <div className='resion'>
                                                        <NavLink to="#" className="exportbutton" onClick={handleExportLoan}
                                                            disabled={isDownloading || !hasPermission('Loan', 'download')}>
                                                            {isDownloading ? (
                                                                <Spinner animation="border" size="sm" />
                                                            ) : (<>Download Pending Loan</>)}
                                                        </NavLink>
                                                    </div>
                                                </div>
                                                <div className="col-md-3 mt-2">
                                                    <div className='resion'>
                                                        <NavLink to="#" className="exportbutton" onClick={handleExportLoanMaster}
                                                            disabled={isDownloadingLmaster || !hasPermission('Loan', 'download')}>
                                                            {isDownloadingLmaster ? (
                                                                <Spinner animation="border" size="sm" />
                                                            ) : (<>Download Loan Master</>)}
                                                        </NavLink>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>

                                {/* 📋 Loan Table */}
                                <div className='col-md-12 col-lg-12'>
                                    <div className='allcustomer table-responsive table-striped mt-3'>
                                        { pageLoading ? <PageLoader text="Loading..." /> : multipleLoans.length > 0  ? (
                                            <>
                                                <table className="styled-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Customer ID</th>
                                                            <th>Borrower Name</th>
                                                            <th>Mobile</th>
                                                            <th>Branch</th>
                                                            <th>Email</th>
                                                            <th>Loan Count</th>
                                                            <th>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {currentLoans.map((customer) => (
                                                            <React.Fragment key={customer.customerId}>
                                                                <tr>
                                                                    <td>{customer.customerId}</td>
                                                                    <td>{customer.borrowerName}</td>
                                                                    <td>{customer.mobileNumber}</td>
                                                                    <td>{customer.branch}</td>
                                                                    <td>{customer.email}</td>
                                                                    <td>{customer.loanCount}</td>
                                                                    <td>
                                                                        <button className="btn btn-sm btn-outline-primary"
                                                                            onClick={() => toggleExpand(customer.customerId)}>
                                                                            {expandedRows[customer.customerId] ? <FaChevronUp /> : <FaChevronDown />}
                                                                        </button>
                                                                    </td>
                                                                </tr>

                                                                {expandedRows[customer.customerId] && (
                                                                    <tr>
                                                                        <td colSpan="7">
                                                                            <table className="table table-bordered inner-table">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>Loan ID</th>
                                                                                        <th>Product Name</th>
                                                                                        <th>Loan Amount</th>
                                                                                        <th>Create Date</th>
                                                                                        <th>Status</th>
                                                                                        <th>View</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {customer.loans.map((loan) => (
                                                                                        <tr key={loan.id}>
                                                                                            <td>{loan.id}</td>
                                                                                            <td>{loan.productName}</td>
                                                                                            <td>{loan.loanAmount}</td>
                                                                                            <td>
                                                                                                {loan.createDate
                                                                                                    ? new Date(loan.createDate).toLocaleDateString()
                                                                                                    : ''}
                                                                                            </td>
                                                                                            <td>
                                                                                                {loan.status === 0
                                                                                                    ? 'Pending'
                                                                                                    : loan.status === 1
                                                                                                        ? 'Active'
                                                                                                        : loan.status === 3
                                                                                                            ? 'Closed'
                                                                                                             : loan.status === 2
                                                                                                            ? 'Rejected'
                                                                                                            : 'Unknown'}
                                                                                            </td>
                                                                                            <td>
                                                                                                <NavLink
                                                                                                    to={`/loanmaster/ViewLoan?customerId=${loan.customerId}&loanId=${loan.id}`}
                                                                                                >
                                                                                                    <FaEye />
                                                                                                </NavLink>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </tbody>
                                                </table>

                                                {/* 🔄 Pagination Controls */}
                                                <div className="pagination mt-3 text-center">
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary me-2"
                                                        disabled={currentPage === 1}
                                                        onClick={() => handlePageChange(currentPage - 1)}
                                                    >
                                                        Previous
                                                    </button>
                                                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => i + startPage).map((page) => (
                                                        <button
                                                            key={page}
                                                            className={`btn btn-sm mx-1 ${page === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    ))}
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary ms-2"
                                                        disabled={currentPage === totalPages}
                                                        onClick={() => handlePageChange(currentPage + 1)}
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="text-center">No customers with multiple loans found.</p>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MultipleLoans;
